import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { COMPASS_GROUP_LABELS } from '../utils/compassDrawScheduler';

// Compass-rose grid position for each of the 8 possible groups. E is the
// main draw and sits in the center, same spot a real compass draw diagram
// puts it; the middle-right cell has no group (there's no "due east"
// consolation bracket — E already owns that name) and is left empty.
const GRID_POSITION = {
  NW: { col: 1, row: 1 },
  N: { col: 2, row: 1 },
  NE: { col: 3, row: 1 },
  W: { col: 1, row: 2 },
  E: { col: 2, row: 2 },
  SW: { col: 1, row: 3 },
  S: { col: 2, row: 3 },
  SE: { col: 3, row: 3 },
};

const GROUP_ACCENT = {
  E: 'border-yellow-500 text-yellow-600',
  W: 'border-blue-500 text-blue-500',
  N: 'border-purple-500 text-purple-500',
  S: 'border-green-500 text-green-600',
  NW: 'border-pink-500 text-pink-500',
  SW: 'border-cyan-500 text-cyan-600',
  NE: 'border-orange-500 text-orange-500',
  SE: 'border-red-500 text-red-500',
};

const matchLabel = (match) =>
  `${match.teamA?.name || 'TBD'} vs ${match.teamB?.isBye ? 'BYE' : (match.teamB?.name || 'TBD')}`;

// One mini-bracket card, shared between the mobile stacked list and the
// desktop compass-rose grid so the two layouts can't drift apart.
function MiniBracketCard({ theme, groupName, group, accent }) {
  return (
    <div className={`${theme.card} rounded-lg p-3 border-2 ${accent.split(' ')[0]} h-full`}>
      <div className={`text-xs font-medium mb-2 ${accent.split(' ')[1]}`}>
        {COMPASS_GROUP_LABELS[groupName]?.toUpperCase()}
        <span className={`ml-1 font-normal ${theme.textMuted}`}>
          ({group.entrantCount} entrant{group.entrantCount === 1 ? '' : 's'})
        </span>
      </div>
      <div className="space-y-2">
        {group.rounds.map((round) => (
          <div key={round.id}>
            <div className={`text-[11px] font-medium ${theme.textSecondary}`}>{round.roundName}</div>
            <div className="space-y-1 mt-0.5">
              {round.matches.map((match) => (
                <div key={match.id} className={`text-xs ${theme.text}`}>
                  {matchLabel(match)}
                  {match.winner && (
                    <span className={`${theme.textMuted}`}> — Winner: {match.winner.name}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Renders the compass-style layout for a generateCompassDraw() result — one
 * mini-bracket card per group that the generator actually populated (a group
 * with fewer than 2 incoming entrants never exists in schedule.groups).
 *
 * Desktop keeps the spatial 3x3 compass-rose grid a physical draw sheet
 * uses. That grid is too cramped to read at phone widths, so mobile falls
 * back to a stacked single-column list instead — same mobile/desktop CSS
 * breakpoint split ScheduleTab.jsx uses for the schedule table (KYN-240).
 * `schedule.activeGroups` is already ordered E-first / feeder-order (see
 * compassDrawScheduler.js), which doubles as a sensible top-to-bottom
 * mobile reading order.
 */
export default function CompassBracketView({ schedule }) {
  const { theme } = useTheme();

  if (!schedule?.groups || !schedule?.activeGroups?.length) return null;

  return (
    <div className={`${theme.backgroundSecondary} rounded-lg p-6`}>
      <h4 className={`font-semibold ${theme.text} mb-1`}>Compass Bracket</h4>
      <p className={`text-xs ${theme.textMuted} mb-4`}>
        East is the main draw; the other directions only appear once they have enough real losers feeding them.
      </p>

      {/* ── MOBILE: stacked list ── */}
      <div className="md:hidden space-y-3">
        {schedule.activeGroups.map((groupName) => {
          const group = schedule.groups[groupName];
          if (!group) return null;
          const accent = GROUP_ACCENT[groupName] || 'border-gray-400 text-gray-500';
          return <MiniBracketCard key={groupName} theme={theme} groupName={groupName} group={group} accent={accent} />;
        })}
      </div>

      {/* ── DESKTOP: compass-rose grid ── */}
      <div
        className="hidden md:grid gap-3"
        style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gridTemplateRows: 'repeat(3, auto)' }}
      >
        {schedule.activeGroups.map((groupName) => {
          const group = schedule.groups[groupName];
          const position = GRID_POSITION[groupName];
          if (!group || !position) return null;
          const accent = GROUP_ACCENT[groupName] || 'border-gray-400 text-gray-500';

          return (
            <div key={groupName} style={{ gridColumnStart: position.col, gridRowStart: position.row }}>
              <MiniBracketCard theme={theme} groupName={groupName} group={group} accent={accent} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
